import ng from 'angular';
import {
    DomainContactValidatorService,
    DomainHandleModelService,
    DomainHelper,
    DomainModelService,
    SentryErrorEmitterService,
} from '@/services';
import {
    DomainContactPanelMode,
    EditPanelRight,
    MoleculeFormEditController,
    OrganismEditFormDomainProductController,
} from '@/atomic-components';
import {
    HandleDefaultLabels,
    HandleTypes,
    HandleMissingContactHintTexts,
    HandleConst,
} from '@/configuration/domain/handle';
import {
    DomainApi,
    ViewTypes,
} from '@/types';

import './domain-contacts.scss';

type Loading = 'loading';
type HasError = 'hasError';
type Loaded = 'loaded';
type ContactState = Loading | HasError | Loaded;

export class OrganEditPanelDomainContactsController {
    public static $inject: string[] = [
        '$timeout',
        '$translate',
        'domainContactValidator',
        'domainHandleModel',
        'domainModel',
    ];

    public panelRight: EditPanelRight;
    public editFormOrganism: OrganismEditFormDomainProductController;
    public domain: DomainApi.Domain;
    public contactSetValidationErrors: unknown[] = [];
    public domains: {tld: string}[];
    public showContactInfoModal = false;
    public loadContactInfo = false;
    public contactInfo: DomainApi.Contact = null;
    public panelMode: DomainContactPanelMode = DomainContactPanelMode.DISPLAY;
    public updateIsOwnerChange: boolean = undefined;
    public designateAgent = false;
    public domainContactList: ViewTypes.DomainContactObject[] = [];
    public contactNotSet: string[] = [];
    public handleTypesConst = HandleTypes;
    public userCanEdit: boolean = false;
    public contacts: {[key: string]: { state: ContactState, handleName: string, additionalInfo: string}} = {
        owner: {
            state: 'loading',
            handleName: this.$translate.instant('TR_060519-4f289c_TR'),
            additionalInfo: this.$translate.instant('TR_060519-4f289c_TR')
        },
        tech: {
            state: 'loading',
            handleName: this.$translate.instant('TR_060519-4f289c_TR'),
            additionalInfo: this.$translate.instant('TR_060519-4f289c_TR')
        },
        admin: {
            state: 'loading',
            handleName: this.$translate.instant('TR_060519-4f289c_TR'),
            additionalInfo: this.$translate.instant('TR_060519-4f289c_TR')
        },
        zone: {
            state: 'loading',
            handleName: this.$translate.instant('TR_060519-4f289c_TR'),
            additionalInfo: this.$translate.instant('TR_060519-4f289c_TR')
        },
    }

    private $editFormMolecule: MoleculeFormEditController;
    private _registrationIndex: number;

    constructor(
        private $timeout: ng.ITimeoutService,
        private $translate: ng.translate.ITranslateService,
        private domainContactValidator: DomainContactValidatorService,
        private domainHandleModel: DomainHandleModelService,
        private domainModel: DomainModelService,
    ) {}

    public $onInit(): void {
        this.panelRight.editPanelButton = HandleTypes.every(
            (handleType) => this.domainContactList.some(
                (contact) => {
                    return contact.contactType.toLocaleLowerCase() === handleType.toLocaleLowerCase();
                }
            )
        );
        this._registrationIndex = this.$editFormMolecule.registerValidator(this);
        this.$editFormMolecule.validatorStatus[this._registrationIndex] = true;
        this.domains = [{ tld: DomainHelper.getTld(this.domain.nameUnicode) }];
        this._fillContacts();
    }

    public get contactsNotSet(): boolean {
        return this.contactNotSet.length > 0;
    }

    public issetHandle = (handleType: string): boolean => {
        return this.domainContactList.some(
            (handle) => handle.contactType.toLocaleLowerCase() === handleType.toLocaleLowerCase()
        );
    };

    public filterNotOwner = (contactType: string): boolean => {
        return 'owner' !== contactType.toLowerCase();
    };

    public validateContactSet = async (): Promise<void> => {
        if (!this._allHandlesSet()) {
            this.contactSetValidationErrors = [];
            this.$editFormMolecule.validatorStatus[this._registrationIndex] = false;
        }

        this.updateIsOwnerChange = undefined;

        const tlds: string[] = [];
        for (const domain of this.domains) {
            tlds.push(domain.tld );
        }

        const contacts = this.domainContactList
            .map(
                (contact) => ({
                    contact: contact.contactId,
                    type: contact.contactType
                })
            );

        await this.domainContactValidator.contactSetUsableFor(
            contacts,
            tlds,
            this.domain.trusteeServiceEnabled,
            this.domain.accountId
        )
        .then(
            async (reply: DomainApi.ContactSetUsableProblem[]) => {
                this.contactSetValidationErrors = reply;
                this.$editFormMolecule.validatorStatus[this._registrationIndex] =
                (this.contactSetValidationErrors.length === 0);

                if (!reply || !this._allHandlesSet()) {
                    return;
                }

                const tmpDomain = ng.copy(this.domain);
                tmpDomain.contacts = this.domainContactList.map(
                    (contact) => {
                        return {
                            contact: contact.contactId,
                            type: contact.contactType
                        };
                    }
                );

                await this.domainModel.domainUpdateIsIcannOwnerChange(tmpDomain)
                .then(
                    (response) => {
                        this.updateIsOwnerChange = response;
                        this.designateAgent = this.designateAgent && response;
                    },
                    () => {
                        this.updateIsOwnerChange = false;
                        this.designateAgent = false;
                    }
                );
            }
        );
    };

    public showContactInfo = async (contactId: string): Promise<void> => {
        if (this.loadContactInfo || this.showContactInfoModal) {
            return;
        }
        this.loadContactInfo = true;
        await this.domainHandleModel.findOne(contactId).then(
            (reply: DomainApi.Contact) => {
                void this.$timeout(() => {
                    this.loadContactInfo = false;
                    this.contactInfo = reply;
                    this.showContactInfoModal = true;
                });
            }
        );
    };

    public closeContactInfoModal = (): void => {
        this.showContactInfoModal = false;
        this.contactInfo = null;
    };

    public getContactId = (handleType: string): string => {
        let handleObject: ViewTypes.DomainContactObject;

        this.domainContactList.some(
            (handle) => {
                if (handle.contactType.toLocaleLowerCase() === handleType.toLocaleLowerCase()) {
                    handleObject = handle;
                    return true;
                }

                return false;
            }
        );

        return handleObject.contactId;
    };

    public getContactLabel = (handleType: string): string => {
        let handleObject: ViewTypes.DomainContactObject;

        this.domainContactList.some(
            (handle) => {
                if (handle.contactType.toLocaleLowerCase() === handleType.toLocaleLowerCase()) {
                    handleObject = handle;
                    return true;
                }

                return false;
            }
        );

        return handleObject.label;
    };

    public getDefaultHandleLabel = (handleType: string): string => {
        let label = '';
        HandleDefaultLabels.some(
            (handle) => {
                if (handle.contactType.toLocaleLowerCase() === handleType.toLocaleLowerCase()) {
                    label = this.$translate.instant(handle.label);

                    return true;
                }

                return false;
            }
        );

        return label;
    };

    public getDefaultHandleMissingHintText = (handleType: string): string => {
        let hintText = '';
        HandleMissingContactHintTexts.some(
            (contact) => {
                if (contact.contactType.toLocaleLowerCase() === handleType.toLocaleLowerCase()) {
                    hintText = this.$translate.instant(contact.label);

                    return true;
                }

                return false;
            }
        );

        return hintText;
    };

    private _allHandlesSet = (): boolean => {
        return this.domainContactList.every((contact) => ![undefined, null, ''].includes(contact.contactId));
    };

    public get disableEditButton() {
        return !this.userCanEdit;
    }

    public get disableSaveButton() {
        return !this._allHandlesSet();
    }

    private _fillContacts = (): void => {
        let contactErrors: {[key: string]: {[key: string]: string}} = {};
        let promises: Promise<any>[] = [];
        let contactTypeOrder: string[] = [];
        for (const contact of this.domainContactList) {
            contactTypeOrder.push(contact.contactType);
            promises.push(this.domainHandleModel.findOne(contact.contactId));
        }
        Promise.allSettled(promises).then((results) => {
            results.forEach(
                (result: PromiseSettledResult<any>, index: number) => {
                    if (result.status === 'fulfilled') {
                        const handle = result.value;
                        let handleName: string;

                        if (['org', 'role'].indexOf(handle.type) >= 0) {
                            handleName = handle.organization
                            + ' (' + this.$translate.instant(HandleConst.HandleTypes.labels[(handle.type as keyof typeof HandleConst.HandleTypes.labels)]) + ')';
                        } else {
                            handleName = handle.name
                            + ' (' + this.$translate.instant(HandleConst.HandleTypes.labels[(handle.type as keyof typeof HandleConst.HandleTypes.labels)]) + ')';

                            if (handle.organization.length > 0) {
                                handleName += ' | ' + handle.organization;
                            }
                        }
                        this.contacts[contactTypeOrder[index]].state = 'loaded';
                        this.contacts[contactTypeOrder[index]].handleName = handleName
                        const handleType = this.$translate.instant(HandleConst.HandleTypes.labels[(handle.type as keyof typeof HandleConst.HandleTypes.labels)]);
                        const addInfo = `${handle.street[0]},
                            ${handle.postalCode} ${handle.city}
                            | ${this.$translate.instant('TR_150419-49d45c_TR')}: ${handleType}
                            | ${this.$translate.instant('dcca5e1c-3bdc-4090-a588-240f81a0e7f8')}: ${handle.handle}`;
                        this.contacts[contactTypeOrder[index]].additionalInfo = addInfo;
                    }

                    if (result.status === 'rejected') {
                        contactErrors[contactTypeOrder[index]] = {error: result.reason.name, contact: this.domainContactList[index].contactId};
                        this.contacts[contactTypeOrder[index]].state = 'hasError';
                        this.contacts[contactTypeOrder[index]].handleName = result.reason.name;
                        this.contacts[contactTypeOrder[index]].additionalInfo = result.reason.name;
                    }
                }
            );
            if (Object.keys(contactErrors).length > 0) {
                SentryErrorEmitterService.sendSentryReport(
                    'Errors in finding domain contacts',
                    {contactErrors},
                    {where: 'domain-contacts.ts'},
                );
            }
        })
    }

    public getContactName(contactType: string): string {
        if (this.contacts[contactType.toLocaleLowerCase()].state === 'hasError') {
            const error = this.contacts[contactType.toLocaleLowerCase()].handleName;
            switch (error) {
                case 'NotFoundError':
                    return this.$translate.instant('TR_200325-803be3_TR');
                default:
                    return this.$translate.instant('TR_290120-8a0350_TR');
            }
        }
        return this.contacts[contactType.toLocaleLowerCase()].handleName;
    }

    public getAdditionalInfo(contactType: string): string {
        if (this.contacts[contactType.toLocaleLowerCase()].state === 'hasError') {
            const error = this.contacts[contactType.toLocaleLowerCase()].additionalInfo;
            switch (error) {
                case 'NotFoundError':
                    return this.$translate.instant('TR_200325-803be3_TR');
                default:
                    return this.$translate.instant('TR_290120-8a0350_TR');
            }
        }
        return this.contacts[contactType.toLocaleLowerCase()].additionalInfo;
    }

    public hasContactName(contactType: string) {
        if (this.contacts[contactType.toLocaleLowerCase()].state === 'loaded') {
            return true;
        }
        return false;
    }
}

export class OrganEditPanelDomainContactsComponent implements ng.IComponentOptions {
    public bindings = {
        designateAgent: '=?',
        domain: '<',
        domainContactList: '<',
        panelRight: '<',
        userCanEdit: '<?'
    };
    public require = {
        $editFormMolecule: '^moleculeFormEdit',
        $editFormOrganism: '^organismEditFormDomainProduct'
    };
    public controller = OrganEditPanelDomainContactsController;
    public template = require('./domain-contacts.html');
}
